import type { shell } from 'punica';
import { isHeadlessScope } from '@punica/editor';
import { emitShellEvent } from '@punica/editor';

import { Helpers } from '../view/helpers.js';
import type { ViewStatus } from '../views/index.js';
import panelTabsCss from './panelTabs.css';

type TabDisplayMode = 'icon' | 'label' | 'both';
type Orientation = 'horizontal' | 'vertical';

interface PanelTabEntry {
  id: string;
  title: string;
  commandId: string;
  iconClass?: string;
  order: number;
  tabElement: HTMLElement;
  iconColEl?: HTMLElement | null;
  labelColEl?: HTMLElement | null;
  userHidden: boolean;
  toolbar?: shell.PanelToolbarAction[];
  toolbarEl?: HTMLElement | null;
  /** Passive indicator published by the owning extension via the ViewRegistry. */
  status?: ViewStatus | null;
}

/** Fallback for the ⋯ button size before it has ever been measured. */
const OVERFLOW_BTN_FALLBACK_SIZE = 28;

/**
 * Scoped stylesheet shared by every tabbed container. Injected once. Selectors
 * key on the root's `data-orientation`, so the bottom (horizontal) strip gets a
 * top accent + the right (vertical) strip a left accent from one stylesheet.
 */
const TABBED_CONTAINER_STYLE_ID = 'punica-tabbed-container-style';

// How long tab registrations must go quiet before the boot-default activation
// picks a tab for a dock nobody activated (see #scheduleDefaultActivation).
const DEFAULT_ACTIVATE_QUIET_MS = 300;

/**
 * Human-readable dock names for the "Move to …" tab context-menu items.
 */
const SLOT_LABELS: Record<string, string> = {
  panel: 'bottom panel',
  secondarySidebar: 'right sidebar'
};

/**
 * The in-flight cross-container view drag, shared by every tabbed container.
 * A target container judges drop eligibility during `dragover` from this —
 * `dataTransfer` is not readable then. Set when a tab drag starts, cleared on
 * `dragend`/`drop`.
 */
let activeViewDrag: { viewId: string; sourceSlot: string } | null = null;

/**
 * Every constructed tabbed container. A tab drag arms the drop zones of ALL
 * containers so a collapsed target dock (e.g. an emptied right sidebar) can be
 * expanded to present a drop area, then restored on `dragend`.
 */
const allContainers: TabbedContainerManager[] = [];

/** Expand any eligible-but-collapsed target dock so it can be dropped onto. */
function armRelocationTargets(): void {
  for (const container of allContainers) container.armDropZone();
}

/** Re-collapse docks expanded only for the drag that received no drop. */
function disarmRelocationTargets(): void {
  for (const container of allContainers) container.disarmDropZone();
}

/**
 * Test-only: forget every constructed container. Production builds exactly one
 * bottom + one right container at module load; tests construct many, so this
 * clears the registry between them to stop stale instances reacting to a drag.
 */
export function __resetTabbedContainersForTest(): void {
  allContainers.length = 0;
}

/**
 * A tabbed dock container. Parameterized by `slotId` + `orientation`:
 *   - bottom panel → HORIZONTAL strip (icon+label, top accent)
 *   - right sidebar → VERTICAL strip (icon-only, left accent, activity-bar style)
 *
 * Both expose the same `PanelTabsApi`. Per-slot preferences (display mode,
 * user-hidden, user order) persist through `PanelLayout` scoped by `slotId`.
 */
class TabbedContainerManager implements shell.TabbedContainerApi {
  readonly slotId: string;
  readonly orientation: Orientation;

  #initialized = false;
  #defaultActivateTimer: ReturnType<typeof setTimeout> | null = null;
  #root: HTMLElement | null = null;
  #tabList: HTMLElement | null = null;
  #toolbarArea: HTMLElement | null = null;
  #overflowBtn: HTMLElement | null = null;
  #overflowMenu: HTMLElement | null = null;
  #showHiddenBtn: HTMLElement | null = null;
  #contextMenu: HTMLElement | null = null;
  #overflowBtnSize: number | null = null;
  #overflowScheduled = false;
  #hiddenIds: Set<string> = new Set();
  #entries: Map<string, PanelTabEntry> = new Map();
  #activeId: string | null = null;
  #displayMode: TabDisplayMode = 'both';
  #dragId: string | null = null;
  #armedExpanded = false;
  #receivedDrop = false;

  constructor(slotId: string, orientation: Orientation) {
    this.slotId = slotId;
    this.orientation = orientation;
    allContainers.push(this);
  }

  /** Element-id prefix. `'panel'` yields the legacy unprefixed ids. */
  #id(suffix: string): string {
    return `${this.slotId}-${suffix}`;
  }

  get #horizontal(): boolean {
    return this.orientation === 'horizontal';
  }

  /** The persistence store, or null when the panelLayout module is absent. */
  #layout(): any {
    return (globalThis as any).punica?.shell?.PanelLayout?.manager ?? null;
  }

  #ensureRoot(): void {
    if (this.#initialized) return;

    const slot = punica.shell.Layout.manager.getSlot(this.slotId as any);
    const host = slot?.content;
    if (!host) {
      // eslint-disable-next-line no-console
      console.warn(
        `[TabbedContainer] host not found for slot "${this.slotId}"`
      );
      return;
    }

    host.innerHTML = '';

    // Adopt the persisted display mode before any tab is built. The vertical
    // (right sidebar) strip is an activity-bar-style icon column, so it defaults
    // to icon-only; the horizontal (bottom) strip defaults to icon + label.
    const defaultMode: TabDisplayMode =
      this.orientation === 'vertical' ? 'icon' : 'both';
    this.#displayMode =
      this.#layout()?.getDisplayMode?.(this.slotId) ?? defaultMode;

    this.#injectStyle();

    const root = Helpers.createBox({
      attrs: {
        id: this.#id('tabs-root'),
        'data-tabbed-container': '',
        'data-orientation': this.orientation
      },
      style: {
        height: '100%',
        width: '100%',
        display: 'flex',
        // Horizontal: strip on top of the views root (column). Vertical: strip
        // on the inner edge, views root beside it (row).
        flexDirection: this.#horizontal ? 'column' : 'row'
      }
    });

    // ── Tab strip ──
    const stripBox = Helpers.createBox({
      style: {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'var(--background-paper)',
        ...(this.#horizontal
          ? { height: '32px', flexDirection: 'row' }
          : { width: '44px', height: '100%', flexDirection: 'column' })
      }
    });

    const tabList = Helpers.createTab({
      orientation: this.orientation,
      // The strip already behaved as a tab list (single selection, per-item
      // `aria-selected`) but carried no role, which left both the selection
      // state and every tab's accessible name unexposed. Naming it makes the
      // ARIA that is already written here real — and it is what lets an
      // icon-only strip be read at all.
      attrs: { role: 'tablist' },
      style: {
        // flex:1 + min-size:0 + overflow:hidden: the strip yields to the
        // fixed-size overflow button / toolbar area, so tabs can never collide
        // with them; the overflow recompute keeps whole tabs from clipping.
        flex: '1 1 auto',
        overflow: 'hidden',
        backgroundColor: 'var(--background-paper)',
        ...(this.#horizontal
          ? { minWidth: '0', height: '100%' }
          : {
              minHeight: '0',
              width: '100%',
              display: 'flex',
              flexDirection: 'column'
            })
      },
      onChange: (value) => {
        if (!value) return;
        this.#selectTab(value, this.#id('tab'));
      }
    });

    // Right-click a tab → Hide; right-click empty strip → display-mode menu.
    stripBox.addEventListener('contextmenu', (e: MouseEvent) => {
      this.#onContextMenu(e);
    });

    // ⋯ overflow trigger: hidden until the recompute finds tabs that do
    // not fit; opens the overflow menu listing them.
    const overflowBtn = Helpers.createIconButton(
      'fa-solid fa-ellipsis',
      () => {
        this.#openOverflowMenu();
      },
      {
        size: 'x-small',
        title: 'More tabs',
        attrs: {
          id: this.#id('tab-overflow-btn'),
          'aria-label': 'More tabs'
        }
      }
    );
    overflowBtn.style.flex = '0 0 auto';
    overflowBtn.style.display = 'none';

    // + trigger: shown only when the user has hidden one or more tabs; opens a
    // menu of the hidden tabs so they can be restored.
    const showHiddenBtn = Helpers.createIconButton(
      'fa-solid fa-plus',
      () => {
        this.#openShowHiddenMenu();
      },
      {
        size: 'x-small',
        title: 'Show hidden tabs',
        attrs: {
          id: this.#id('tab-show-hidden-btn'),
          'aria-label': 'Show hidden tabs'
        }
      }
    );
    showHiddenBtn.style.flex = '0 0 auto';
    showHiddenBtn.style.display = 'none';

    // Per-view toolbar area: each view that declares toolbar actions gets its
    // own container here; only the active view's is displayed.
    const toolbarArea = Helpers.createBox({
      attrs: { id: this.#id('toolbar-area') },
      style: {
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        padding: '0 var(--spacing-8)'
      }
    });

    stripBox.appendChild(tabList);
    stripBox.appendChild(showHiddenBtn);
    stripBox.appendChild(overflowBtn);
    stripBox.appendChild(toolbarArea);

    const overflowMenu = Helpers.createMenu({
      attrs: { id: this.#id('tab-overflow-menu') }
    });
    overflowMenu.addEventListener('close', () => {
      overflowMenu.removeAttribute('open');
    });
    overflowMenu.addEventListener('menu-item-click', (e: any) => {
      const id = e?.detail?.data as string | undefined;
      if (!id) return;
      e.stopPropagation?.();
      overflowMenu.removeAttribute('open');
      // Same user-gesture path as a direct tab click; activateTabVisual's
      // recompute then un-hides the chosen tab (active tab is reserved).
      this.#selectTab(id, this.#id('tab-overflow'));
    });

    // Shared context menu (hide / display-mode / show-hidden actions).
    const contextMenu = Helpers.createMenu({
      attrs: { id: this.#id('tab-context-menu') }
    });
    contextMenu.addEventListener('close', () => {
      contextMenu.removeAttribute('open');
    });
    contextMenu.addEventListener('menu-item-click', (e: any) => {
      const data = e?.detail?.data as string | undefined;
      contextMenu.removeAttribute('open');
      if (data) this.#handleContextAction(data);
    });

    const viewsRoot = document.createElement('div');
    viewsRoot.id = this.#id('views-root');
    viewsRoot.setAttribute('data-views-root', '');
    viewsRoot.style.flex = '1';
    if (this.#horizontal) {
      viewsRoot.style.height = 'calc(100% - 32px)';
      viewsRoot.style.width = '100%';
    } else {
      viewsRoot.style.height = '100%';
      viewsRoot.style.width = 'calc(100% - 44px)';
    }

    root.appendChild(stripBox);
    root.appendChild(viewsRoot);
    root.appendChild(overflowMenu);
    root.appendChild(contextMenu);

    host.appendChild(root);

    // The whole container is a drop target for a view dragged out of the OTHER
    // container — a right-sidebar view onto the bottom panel or vice-versa.
    this.#wireRelocationDropZone(root);

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        this.#scheduleOverflowRecompute();
      });
      observer.observe(stripBox);
    }

    this.#root = root;
    this.#tabList = tabList;
    this.#toolbarArea = toolbarArea;
    this.#overflowBtn = overflowBtn;
    this.#overflowMenu = overflowMenu;
    this.#showHiddenBtn = showHiddenBtn;
    this.#contextMenu = contextMenu;
    this.#initialized = true;
  }

  #injectStyle(): void {
    if (typeof document === 'undefined') return;
    if (document.getElementById(TABBED_CONTAINER_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = TABBED_CONTAINER_STYLE_ID;
    style.textContent = panelTabsCss;
    document.head.appendChild(style);
  }

  /**
   * Route a tab selection (strip click, overflow-menu pick, or the boot
   * default below) through the single reveal choke point when the tab is a
   * registered view; fall back to the legacy command path for pre-migration
   * tabs. `kind` attributes the reveal: 'user-gesture' for clicks, 'host'
   * when the shell itself picks the boot default.
   */
  #selectTab(
    value: string,
    gestureSource: string,
    kind: 'user-gesture' | 'host' = 'user-gesture'
  ): void {
    const viewsManager = (globalThis as any).punica?.shell?.Views?.manager;
    if (viewsManager?.getPlacement?.(value)) {
      void viewsManager
        .reveal(value, {
          kind,
          id: `${gestureSource}:${value}`
        })
        .catch((err: unknown) => {
          // eslint-disable-next-line no-console
          console.warn('[TabbedContainer] reveal failed for tab', value, err);
        });
      this.activateTabVisual(value);
      return;
    }
    this.setActive(value);
  }

  /**
   * Boot default for a dock nobody activated.
   *
   * Declaratively registered tabs arrive with `autoActivate: false` (loading
   * an extension must never steal the visible panel), so a dock whose tabs
   * ALL arrive that way finishes the boot with a filled tab strip over an
   * empty body — which reads as broken, not as neutral. Once registrations go
   * quiet, select the leftmost visible tab through the same reveal path a
   * click takes, so its provider actually renders. Debounced per registration
   * so the pick respects the final tab order rather than the load order, and
   * a no-op the moment anything is active — this can never steal a view a
   * user (or a reveal) already chose.
   */
  #scheduleDefaultActivation(): void {
    if (this.#defaultActivateTimer !== null) {
      clearTimeout(this.#defaultActivateTimer);
    }
    this.#defaultActivateTimer = setTimeout(() => {
      this.#defaultActivateTimer = null;
      this.#activateDefaultTab();
    }, DEFAULT_ACTIVATE_QUIET_MS);
  }

  #activateDefaultTab(): void {
    if (this.#activeId) return;
    // A detached strip is a torn-down container (workspace switch, tests) —
    // its stale timer must not touch the live mocks/managers of a successor.
    if (!this.#tabList?.isConnected) return;
    const first = Array.from(this.#entries.values())
      .filter((e) => !e.userHidden)
      .sort((a, b) => a.order - b.order)[0];
    if (!first) return;
    this.#selectTab(first.id, 'panel-default', 'host');
  }

  open(options: {
    id: string;
    title: string;
    commandId: string;
    iconClass?: string;
    order?: number;
    autoActivate?: boolean;
    toolbar?: shell.PanelToolbarAction[];
  }): void {
    this.#ensureRoot();
    if (!this.#tabList) return;

    const existing = this.#entries.get(options.id);
    if (existing) {
      // Activate if caller explicitly requested it AND this tab isn't already
      // the active one. The second guard breaks the re-entrant loop that would
      // otherwise form: setActive → executeCommand → open → setActive → …
      if (
        options.autoActivate !== false &&
        this.#activeId !== options.id &&
        !this.#blockHeadlessActivation(options.id, 'PanelTabs.open')
      ) {
        this.setActive(options.id);
      }
      return;
    }

    const row = Helpers.createRow({
      alignItems: 'center',
      wrap: 'nowrap',
      spacing: '0.5'
    });

    let iconColEl: HTMLElement | null = null;
    if (options.iconClass) {
      // Manifests declare icons as full `<i class="...">` HTML (same as the
      // ActivityBar, which renders them via innerHTML). Accept that form, and
      // still tolerate a bare class string for programmatic callers.
      const raw = options.iconClass.trim();
      const icon = Helpers.createIcon(
        raw.startsWith('<') ? { innerHTML: raw } : { iconClass: raw }
      );
      iconColEl = Helpers.createCol({
        attrs: { 'data-col': 'icon' },
        children: [icon]
      });
      row.appendChild(iconColEl);
    }

    const labelCol = Helpers.createCol({
      attrs: { 'data-col': 'label' },
      children: [
        Helpers.createTypography(options.title, { attrs: { truncate: '1' } })
      ]
    });
    row.appendChild(labelCol);

    const tabItem = Helpers.createTabItem({
      value: options.id,
      attrs: { title: options.title, role: 'tab' },
      style: {
        flex: '0 0 auto',
        ...(this.#horizontal
          ? { maxWidth: '160px', padding: 'var(--spacing-6) var(--spacing-8)' }
          : {
              width: '100%',
              padding: 'var(--spacing-8) var(--spacing-6)',
              // punica-tab-item's shadow :host hardcodes height:100% +
              // flex-grow:1 (right for a horizontal bar). In the vertical
              // column that stretches each item to the full strip height, so
              // size it to content instead — an activity-bar-style icon
              // button. Inline styles beat the shadow rule.
              height: 'auto',
              flexGrow: '0'
            })
      },
      children: [row]
    });
    (tabItem as HTMLElement).draggable = true;

    const userHidden =
      this.#layout()?.isTabHidden?.(options.id, this.slotId) === true;
    if (userHidden) tabItem.setAttribute('data-user-hidden', '');

    // Effective order: persisted user order (if any) wins; otherwise manifest.
    const order = this.#effectiveOrder(options.id, options.order);

    // Insert according to order (ascending).
    const existingEntries = Array.from(this.#entries.values()).sort(
      (a, b) => a.order - b.order
    );
    const beforeEntry = existingEntries.find((e) => e.order > order);

    if (beforeEntry) {
      this.#tabList.insertBefore(tabItem, beforeEntry.tabElement);
    } else {
      this.#tabList.appendChild(tabItem);
    }

    const entry: PanelTabEntry = {
      id: options.id,
      title: options.title,
      commandId: options.commandId,
      iconClass: options.iconClass,
      order,
      tabElement: tabItem,
      iconColEl,
      labelColEl: labelCol,
      userHidden,
      toolbar: options.toolbar,
      toolbarEl: null
    };

    if (options.toolbar?.length && this.#toolbarArea) {
      entry.toolbarEl = this.#buildToolbar(options.id, options.toolbar);
      this.#toolbarArea.appendChild(entry.toolbarEl);
    }

    this.#entries.set(options.id, entry);
    this.#applyDisplayMode(entry);
    this.#wireTabDnD(entry);
    this.#updateShowHiddenBtn();
    this.#scheduleOverflowRecompute();

    // If this is the first tab, make it active by default unless the caller
    // explicitly opted out via autoActivate: false. Blocked during headless
    // activation: a capability call must never steal the visible panel. A
    // user-hidden tab is never auto-activated.
    const autoActivate = options.autoActivate;
    if (
      !this.#activeId &&
      !userHidden &&
      autoActivate !== false &&
      !this.#blockHeadlessActivation(options.id, 'PanelTabs.open')
    ) {
      this.setActive(options.id);
    }

    // Opted-out registrations still count toward the boot default: when the
    // dock ends up with tabs but no active view, pick one after the dust
    // settles (see #scheduleDefaultActivation). The timer fires outside any
    // headless activation scope, so the guard above keeps its meaning.
    if (!this.#activeId && !userHidden) {
      this.#scheduleDefaultActivation();
    }
  }

  /** Persisted user order position, else the manifest order (default 1000). */
  #effectiveOrder(id: string, manifestOrder?: number): number {
    const userOrder = this.#layout()?.getTabOrder?.(this.slotId) as
      string[] | undefined;
    if (userOrder && userOrder.length) {
      const idx = userOrder.indexOf(id);
      if (idx >= 0) return idx;
      // Not in the user order → sort after all user-ordered tabs, by manifest.
      return userOrder.length * 1000 + (manifestOrder ?? 1000);
    }
    return manifestOrder ?? 1000;
  }

  /** Toggle the icon / label columns of a tab per the current display mode. */
  #applyDisplayMode(entry: PanelTabEntry): void {
    const mode = this.#displayMode;
    const hasIcon = !!entry.iconColEl;
    // Icon-only with no icon would render an empty tab — fall back to label.
    const showIcon = hasIcon && (mode === 'icon' || mode === 'both');
    const showLabel = mode === 'label' || mode === 'both' || !hasIcon;
    if (entry.iconColEl) entry.iconColEl.style.display = showIcon ? '' : 'none';
    if (entry.labelColEl) {
      entry.labelColEl.style.display = showLabel ? '' : 'none';
    }
  }

  /**
   * Build the per-view toolbar container: one icon button per declared
   * action, hidden until the owning view becomes the active view. Clicks
   * dispatch through the kernel command manager — the single command choke
   * point, which lazy-activates the owning extension.
   */
  #buildToolbar(
    viewId: string,
    actions: shell.PanelToolbarAction[]
  ): HTMLElement {
    const container = document.createElement('div');
    container.dataset.viewId = viewId;
    container.style.display = 'none';
    container.style.alignItems = 'center';
    container.style.gap = 'var(--spacing-4)';

    for (const action of actions) {
      if (!action?.command) continue;
      // Empty icon class: a toolbar action declares its icon as finished
      // markup, so it goes through the `innerHTML` path instead.
      const btn = Helpers.createIconButton(
        '',
        () => {
          try {
            const anyWindow = window as any;
            const commands = anyWindow?.punica?.kernel?.Commands?.manager;
            if (commands && typeof commands.executeCommand === 'function') {
              commands.executeCommand(action.command);
            }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn(
              '[TabbedContainer] Failed to execute toolbar command',
              action.command,
              err
            );
          }
        },
        {
          size: 'x-small',
          title: action.title ?? action.command,
          attrs: {
            'aria-label': action.title ?? action.command,
            'data-action-id': action.id
          },
          style: { flex: '0 0 auto' },
          innerHTML: action.icon ?? ''
        }
      );
      container.appendChild(btn);
    }

    return container;
  }

  /**
   * TRANSITIONAL guard: returns true (and audits) when a focus-stealing
   * tab activation is attempted while a headless extension activation is
   * in flight. Registering the tab itself stays allowed — only the
   * visible-panel switch is blocked. Removed with the legacy path once
   * the fleet migrates to view providers + shell.revealView.
   */
  #blockHeadlessActivation(id: string, api: string): boolean {
    if (!isHeadlessScope()) return false;
    emitShellEvent({
      name: 'shell.view.revealBlocked',
      source: 'shell:views',
      kind: 'warn',
      payload: { viewId: id, slot: this.slotId, api }
    });
    // eslint-disable-next-line no-console
    console.warn(
      `[Shell] UI mutation during headless activation blocked: ${api}("${id}"). ` +
        'Declare the tab without autoActivate and reveal via the shell.revealView capability instead.'
    );
    return true;
  }

  /**
   * Paint a tab's passive indicator: the icon takes the status color (CSS keys
   * on `data-status`) and the words go into the tooltip AND the accessible
   * name. Both strips can be icon-only — the vertical dock always is, and the
   * bottom strip when the user picks "Show icons only" — and there the color
   * would otherwise be the only channel, which is not one everybody can read.
   *
   * The ViewRegistry owns the status and calls this; extensions publish through
   * `context.ui.setViewStatus`, never here.
   */
  setTabStatus(id: string, status: ViewStatus | null): void {
    const entry = this.#entries.get(id);
    if (!entry) return;
    entry.status = status;
    const level = status?.level && status.level !== 'none' ? status.level : null;
    if (level) {
      entry.tabElement.setAttribute('data-status', level);
    } else {
      entry.tabElement.removeAttribute('data-status');
    }
    const name = status?.detail
      ? `${entry.title} · ${status.detail}`
      : entry.title;
    entry.tabElement.setAttribute('title', name);
    entry.tabElement.setAttribute('aria-label', name);
  }

  /**
   * Update the tab strip visual selection WITHOUT executing the tab's
   * command. Used by the ViewRegistry when a provider-rendered view is
   * revealed — rendering already happened through the provider, so the
   * legacy command path must not re-enter.
   */
  activateTabVisual(id: string): void {
    if (!this.#tabList) return;
    const entry = this.#entries.get(id);
    if (!entry) return;

    this.#activeId = id;
    this.#tabList.setAttribute('value', id);

    const items =
      this.#tabList.querySelectorAll<HTMLElement>('punica-tab-item');
    items.forEach((item) => {
      const value = item.getAttribute('value');
      // Strip the component's own `selected` state (it draws a bottom/right
      // border indicator we don't want) — our `active` accent is the only one.
      item.removeAttribute('selected');
      if (value === id) {
        item.setAttribute('active', '');
        item.setAttribute('aria-selected', 'true');
      } else {
        item.removeAttribute('active');
        item.setAttribute('aria-selected', 'false');
      }
    });

    // Swap the per-view toolbar: only the active view's actions show.
    this.#entries.forEach((e) => {
      if (e.toolbarEl) {
        e.toolbarEl.style.display = e.id === id ? 'flex' : 'none';
      }
    });

    // The newly active tab may currently be collapsed into the overflow
    // menu — recompute so the active-tab reservation reveals it.
    this.recomputeOverflow();
  }

  close(id: string, options?: { silent?: boolean }): void {
    if (!this.#tabList) return;
    const entry = this.#entries.get(id);
    if (!entry) return;

    if (entry.tabElement.parentElement === this.#tabList) {
      this.#tabList.removeChild(entry.tabElement);
    }
    if (entry.toolbarEl) {
      entry.toolbarEl.remove();
      entry.toolbarEl = null;
    }
    this.#hiddenIds.delete(id);
    this.#entries.delete(id);
    this.#scheduleOverflowRecompute();

    if (this.#activeId === id) {
      const remainingEntries = Array.from(this.#entries.values())
        .filter((e) => !e.userHidden)
        .sort((a, b) => a.order - b.order);
      this.#activeId = null;
      // `silent` (used by the ViewRegistry when hiding an on-demand panel)
      // removes the tab without promoting a sibling here: the registry
      // decides what the dock shows next (a remaining sibling via `reveal`,
      // or a collapsed region when no tab is left).
      if (!options?.silent && remainingEntries.length > 0) {
        this.setActive(remainingEntries[0].id);
      } else {
        this.#tabList.removeAttribute('value');
      }
    }
  }

  closeAll(): void {
    if (!this.#tabList) return;
    Array.from(this.#entries.keys()).forEach((id) => this.close(id));
  }

  setActive(id: string): void {
    if (!this.#tabList) return;
    const entry = this.#entries.get(id);
    if (!entry) return;

    if (this.#blockHeadlessActivation(id, 'PanelTabs.setActive')) return;

    this.activateTabVisual(id);

    // Execute associated command to let the owning extension render its panel.
    // Declarative provider-backed tabs may have no command — nothing to run.
    if (!entry.commandId) return;
    try {
      const anyWindow = window as any;
      const commands = anyWindow?.punica?.kernel?.Commands?.manager;
      if (commands && typeof commands.executeCommand === 'function') {
        commands.executeCommand(entry.commandId);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        '[TabbedContainer] Failed to execute command',
        entry.commandId,
        err
      );
    }
  }

  getActiveId(): string | null {
    return this.#activeId;
  }

  // ── Display mode / user hide-show / user order (persisted per slot) ──

  getDisplayMode(): TabDisplayMode {
    return this.#displayMode;
  }

  setDisplayMode(mode: TabDisplayMode): void {
    this.#displayMode = mode;
    this.#entries.forEach((entry) => this.#applyDisplayMode(entry));
    void this.#layout()?.setDisplayMode?.(mode, { slot: this.slotId });
    this.#scheduleOverflowRecompute();
  }

  hideTab(id: string): void {
    const entry = this.#entries.get(id);
    if (!entry || entry.userHidden) return;
    entry.userHidden = true;
    entry.tabElement.setAttribute('data-user-hidden', '');
    void this.#layout()?.setTabHidden?.(id, true, { slot: this.slotId });
    this.#updateShowHiddenBtn();

    if (this.#activeId === id) {
      this.#activeId = null;
      this.#tabList?.removeAttribute('value');
      entry.tabElement.removeAttribute('active');
      const next = Array.from(this.#entries.values())
        .filter((e) => !e.userHidden)
        .sort((a, b) => a.order - b.order)[0];
      if (next) this.#selectTab(next.id, this.#id('tab-hide'));
    }
    this.#scheduleOverflowRecompute();
  }

  showTab(id: string): void {
    const entry = this.#entries.get(id);
    if (!entry || !entry.userHidden) return;
    entry.userHidden = false;
    entry.tabElement.removeAttribute('data-user-hidden');
    void this.#layout()?.setTabHidden?.(id, false, { slot: this.slotId });
    this.#updateShowHiddenBtn();
    this.#scheduleOverflowRecompute();
    this.#selectTab(id, this.#id('tab-show'));
  }

  setUserOrder(ids: string[]): void {
    if (!this.#tabList) return;
    // Reindex known entries to the given position; reorder DOM to match.
    ids.forEach((id, i) => {
      const entry = this.#entries.get(id);
      if (entry) {
        entry.order = i;
        this.#tabList!.appendChild(entry.tabElement);
      }
    });
    // Any entry not named in `ids` keeps its relative order after the listed set.
    Array.from(this.#entries.values())
      .filter((e) => !ids.includes(e.id))
      .sort((a, b) => a.order - b.order)
      .forEach((entry, i) => {
        entry.order = ids.length + i;
        this.#tabList!.appendChild(entry.tabElement);
      });
    void this.#layout()?.setTabOrder?.(this.#orderedIds(), {
      slot: this.slotId
    });
    this.#scheduleOverflowRecompute();
  }

  listTabs(): Array<{ id: string; title: string; userHidden: boolean }> {
    return Array.from(this.#entries.values())
      .sort((a, b) => a.order - b.order)
      .map((e) => ({ id: e.id, title: e.title, userHidden: e.userHidden }));
  }

  /** Current tab ids in DOM/order sequence. */
  #orderedIds(): string[] {
    return Array.from(this.#entries.values())
      .sort((a, b) => a.order - b.order)
      .map((e) => e.id);
  }

  // ── Drag to reorder ──

  #wireTabDnD(entry: PanelTabEntry): void {
    const el = entry.tabElement;
    el.addEventListener('dragstart', (e: DragEvent) => {
      this.#dragId = entry.id;
      // Publish the drag for the cross-container drop zones; the source slot
      // lets a target tell relocation (different slot) from reorder (same slot).
      activeViewDrag = { viewId: entry.id, sourceSlot: this.slotId };
      e.dataTransfer?.setData('application/x-punica-view', entry.id);
      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
      armRelocationTargets();
    });
    el.addEventListener('dragend', () => {
      this.#dragId = null;
      activeViewDrag = null;
      disarmRelocationTargets();
    });
    el.addEventListener('dragover', (e: DragEvent) => {
      if (this.#dragId && this.#dragId !== entry.id) e.preventDefault();
    });
    el.addEventListener('drop', (e: DragEvent) => {
      e.preventDefault();
      const dragId = this.#dragId;
      this.#dragId = null;
      if (!dragId || dragId === entry.id) return;
      // Compare the pointer against the target's midpoint along the strip axis.
      const pointer = this.#horizontal ? e.clientX : e.clientY;
      this.#reorderRelativeTo(dragId, entry.id, pointer);
    });
  }

  /** Move `dragId` before/after `targetId` based on pointer vs its midpoint. */
  #reorderRelativeTo(dragId: string, targetId: string, pointer: number): void {
    const targetEntry = this.#entries.get(targetId);
    if (!targetEntry) return;
    const rect = targetEntry.tabElement.getBoundingClientRect();
    const insertAfter = this.#horizontal
      ? pointer > rect.left + rect.width / 2
      : pointer > rect.top + rect.height / 2;

    const order = this.#orderedIds().filter((id) => id !== dragId);
    const idx = order.indexOf(targetId);
    const at = insertAfter ? idx + 1 : idx;
    order.splice(at, 0, dragId);
    this.setUserOrder(order);
  }

  // ── Cross-container relocation (drag a view between dock areas) ──

  /**
   * Make `zone` a drop target for a view dragged out of the OTHER container.
   * Same-container drags (reorder) are handled per tab item and stay inert
   * here because eligibility requires a different source slot.
   */
  #wireRelocationDropZone(zone: HTMLElement): void {
    zone.addEventListener('dragover', (e: DragEvent) => {
      if (!this.#relocationEligible()) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      this.#root?.setAttribute('data-drop-target', '');
    });
    zone.addEventListener('dragleave', (e: DragEvent) => {
      // Ignore moves between the container's own children; clear only when the
      // pointer actually leaves the container.
      if (zone.contains(e.relatedTarget as Node | null)) return;
      this.#root?.removeAttribute('data-drop-target');
    });
    zone.addEventListener('drop', (e: DragEvent) => {
      this.#root?.removeAttribute('data-drop-target');
      const drag = activeViewDrag;
      if (!drag || !this.#relocationEligible()) return;
      e.preventDefault();
      e.stopPropagation();
      // Mark BEFORE clearing the drag so `disarmDropZone` keeps this (now
      // populated) dock expanded rather than re-collapsing it.
      this.#receivedDrop = true;
      activeViewDrag = null;
      this.#moveView(drag.viewId, this.slotId);
    });
  }

  /**
   * Expand this dock if it is an eligible drop target for the in-flight drag
   * but currently collapsed to zero size (e.g. an emptied right sidebar), so
   * the user has somewhere to drop. Restored by `disarmDropZone` if no drop
   * lands here.
   */
  armDropZone(): void {
    this.#receivedDrop = false;
    if (!this.#relocationEligible()) return;
    // Make sure the container DOM (and its drop zone) exists before showing it.
    this.#ensureRoot();
    const layout = punica.shell.Layout.manager;
    if (layout.isSlotCollapsed?.(this.slotId as any)) {
      layout.setSlotCollapsed(this.slotId as any, false);
      this.#armedExpanded = true;
    }
  }

  /** Undo an `armDropZone` expansion when the drag ended without dropping here. */
  disarmDropZone(): void {
    this.#root?.removeAttribute('data-drop-target');
    // Only re-collapse a dock WE expanded (#armedExpanded) when no view was
    // dropped onto it (#receivedDrop) — restoring its pre-drag collapsed state.
    if (this.#armedExpanded && !this.#receivedDrop) {
      punica.shell.Layout.manager.setSlotCollapsed(this.slotId as any, true);
    }
    this.#armedExpanded = false;
    this.#receivedDrop = false;
  }

  /** Whether the in-flight drag is a view that may relocate INTO this slot. */
  #relocationEligible(): boolean {
    const drag = activeViewDrag;
    if (!drag || drag.sourceSlot === this.slotId) return false;
    const placement = (globalThis as any).punica?.shell?.Views?.manager?.getPlacement?.(
      drag.viewId
    );
    const allowed = placement?.allowedSlots as string[] | undefined;
    return Array.isArray(allowed) && allowed.includes(this.slotId);
  }

  /** Relocate a view to `targetSlot` through the single move choke point. */
  #moveView(viewId: string, targetSlot: string): void {
    const viewsManager = (globalThis as any).punica?.shell?.Views?.manager;
    if (typeof viewsManager?.moveView !== 'function') return;
    void viewsManager
      .moveView(viewId, targetSlot, {
        kind: 'user-gesture',
        id: `view-dnd:${viewId}`
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.warn('[TabbedContainer] moveView failed', viewId, err);
      });
  }

  // ── Context menus ──

  #onContextMenu(e: MouseEvent): void {
    const menu = this.#contextMenu;
    if (!menu) return;
    e.preventDefault();

    const tabItem = (e.target as HTMLElement | null)?.closest?.(
      'punica-tab-item'
    ) as HTMLElement | null;

    menu.innerHTML = '';
    if (tabItem) {
      const id = tabItem.getAttribute('value');
      if (id) {
        this.#addMenuItem(menu, `hide:${id}`, 'Hide tab');
        // "Move to …" for every allowed slot other than the current one — the
        // right-click equivalent of dragging the tab to the other dock area.
        const placement = (globalThis as any).punica?.shell?.Views?.manager?.getPlacement?.(
          id
        );
        const allowed = (placement?.allowedSlots as string[] | undefined) ?? [];
        allowed
          .filter((slot) => slot !== this.slotId)
          .forEach((slot) => {
            this.#addMenuItem(
              menu,
              `move:${id}:${slot}`,
              `Move to ${SLOT_LABELS[slot] ?? slot}`
            );
          });
      }
    }
    // Display-mode radios always available.
    this.#addMenuItem(
      menu,
      'display:both',
      `${this.#displayMode === 'both' ? '● ' : ''}Show icons and labels`
    );
    this.#addMenuItem(
      menu,
      'display:icon',
      `${this.#displayMode === 'icon' ? '● ' : ''}Show icons only`
    );
    this.#addMenuItem(
      menu,
      'display:label',
      `${this.#displayMode === 'label' ? '● ' : ''}Show labels only`
    );

    this.#openMenuAt(menu, e.clientX, e.clientY);
  }

  #handleContextAction(data: string): void {
    const parts = data.split(':');
    const action = parts[0];
    if (action === 'hide' && parts[1]) {
      this.hideTab(parts[1]);
    } else if (action === 'display' && parts[1]) {
      this.setDisplayMode(parts[1] as TabDisplayMode);
    } else if (action === 'show' && parts[1]) {
      this.showTab(parts[1]);
    } else if (action === 'move' && parts[1] && parts[2]) {
      this.#moveView(parts[1], parts[2]);
    }
  }

  #addMenuItem(menu: HTMLElement, data: string, label: string): void {
    // `createMenuItem` wraps `text` in a punica-typography of its own — the
    // same node this used to build by hand.
    menu.appendChild(
      Helpers.createMenuItem({
        text: label,
        attrs: { 'data-item': data, 'aria-label': label }
      })
    );
  }

  #openMenuAt(menu: HTMLElement, x: number, y: number): void {
    // A context menu opens at the pointer, which has no element to measure —
    // hence the bare rect rather than an anchor.
    Helpers.openMenuAt(menu, {
      anchor: { top: y, left: x, bottom: y },
      placement: 'bottom'
    });
  }

  #openShowHiddenMenu(): void {
    const menu = this.#contextMenu;
    const btn = this.#showHiddenBtn;
    if (!menu || !btn) return;
    menu.innerHTML = '';
    Array.from(this.#entries.values())
      .filter((e) => e.userHidden)
      .sort((a, b) => a.order - b.order)
      .forEach((e) => this.#addMenuItem(menu, `show:${e.id}`, e.title));
    const rect = btn.getBoundingClientRect();
    this.#openMenuAt(menu, rect.left, rect.bottom);
  }

  #updateShowHiddenBtn(): void {
    const btn = this.#showHiddenBtn;
    if (!btn) return;
    const anyHidden = Array.from(this.#entries.values()).some(
      (e) => e.userHidden
    );
    btn.style.display = anyHidden ? 'inline-flex' : 'none';
  }

  /** Coalesce overflow recomputes (resize bursts, batch tab opens). */
  #scheduleOverflowRecompute(): void {
    if (this.#overflowScheduled) return;
    this.#overflowScheduled = true;
    const run = () => {
      this.#overflowScheduled = false;
      this.recomputeOverflow();
    };
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(run);
    } else {
      setTimeout(run, 0);
    }
  }

  /** Strip's available size along its axis (width for horizontal, height for vertical). */
  #stripClientSize(): number {
    const tabList = this.#tabList;
    if (!tabList) return 0;
    return this.#horizontal ? tabList.clientWidth : tabList.clientHeight;
  }

  /** A tab item's size along the strip axis. */
  #itemSize(el: HTMLElement): number {
    return this.#horizontal ? el.offsetWidth : el.offsetHeight;
  }

  /**
   * Collapse tabs that do not fit the strip, strictly from the end, into
   * the ⋯ overflow menu. The active tab's size is reserved up front so it
   * stays visible even when its position lies past the cutoff. Runs
   * synchronously: everything is un-hidden for measurement and re-hidden in
   * the same task, before paint. User-hidden tabs are excluded entirely
   * (they never flash, never consume the budget).
   */
  recomputeOverflow(): void {
    const tabList = this.#tabList;
    if (!tabList) return;

    const items = Array.from(
      tabList.querySelectorAll<HTMLElement>('punica-tab-item')
    ).filter((item) => !item.hasAttribute('data-user-hidden'));

    items.forEach((item) => {
      item.style.display = '';
    });

    if (items.length === 0) {
      this.#hiddenIds.clear();
      this.#setOverflowButtonVisible(false);
      return;
    }

    const available = this.#stripClientSize();
    if (available <= 0) {
      // Not laid out yet (or headless) — keep everything visible.
      this.#hiddenIds.clear();
      this.#setOverflowButtonVisible(false);
      return;
    }

    const sizes = items.map((item) => this.#itemSize(item));
    const total = sizes.reduce((sum, w) => sum + w, 0);
    if (total <= available) {
      this.#hiddenIds.clear();
      this.#setOverflowButtonVisible(false);
      return;
    }

    const budget = available - this.#overflowButtonSize();
    const activeIndex = items.findIndex(
      (item) => item.getAttribute('value') === this.#activeId
    );

    // Reserve the active tab first — it must never be hidden.
    let used = activeIndex >= 0 ? sizes[activeIndex] : 0;
    const hidden = new Set<string>();
    let fits = true;

    items.forEach((item, i) => {
      if (i === activeIndex) return;
      if (fits && used + sizes[i] <= budget) {
        used += sizes[i];
        return;
      }
      // First non-fit: this and every later non-active tab collapses.
      fits = false;
      item.style.display = 'none';
      const value = item.getAttribute('value');
      if (value) hidden.add(value);
    });

    this.#hiddenIds = hidden;
    this.#setOverflowButtonVisible(hidden.size > 0);
  }

  #setOverflowButtonVisible(visible: boolean): void {
    const btn = this.#overflowBtn;
    if (!btn) return;
    btn.style.display = visible ? 'inline-flex' : 'none';
    if (!visible) {
      this.#overflowMenu?.removeAttribute('open');
    }
  }

  #overflowButtonSize(): number {
    if (this.#overflowBtnSize != null) return this.#overflowBtnSize;
    const btn = this.#overflowBtn;
    if (!btn) return OVERFLOW_BTN_FALLBACK_SIZE;
    const prevDisplay = btn.style.display;
    btn.style.display = 'inline-flex';
    const size = this.#itemSize(btn) || OVERFLOW_BTN_FALLBACK_SIZE;
    btn.style.display = prevDisplay;
    this.#overflowBtnSize = size;
    return size;
  }

  #openOverflowMenu(): void {
    const menu = this.#overflowMenu;
    const btn = this.#overflowBtn;
    const tabList = this.#tabList;
    if (!menu || !btn || !tabList || this.#hiddenIds.size === 0) return;

    menu.innerHTML = '';

    // DOM order already encodes tab order (open() inserts sorted).
    tabList.querySelectorAll<HTMLElement>('punica-tab-item').forEach((item) => {
      const id = item.getAttribute('value');
      if (!id || !this.#hiddenIds.has(id)) return;
      const entry = this.#entries.get(id);
      if (!entry) return;

      menu.appendChild(
        Helpers.createMenuItem({
          text: entry.title,
          attrs: { 'data-item': id, 'aria-label': entry.title }
        })
      );
    });

    Helpers.openMenuAt(menu, { anchor: btn, placement: 'bottom' });
  }
}

/**
 * Bottom (horizontal) tabbed container — the back-compat `PanelTabsManager`.
 * Kept as a named export + default so existing imports and tests are stable.
 */
class PanelTabsManager extends TabbedContainerManager {
  constructor() {
    super('panel', 'horizontal');
  }
}

const bottom = new PanelTabsManager();
const right = new TabbedContainerManager('secondarySidebar', 'vertical');

/** Slot → container registry (`punica.shell.PanelContainers`). */
const panelContainers: shell.PanelContainersApi = {
  bottom,
  right,
  forSlot(slot: string): shell.TabbedContainerApi | null {
    if (slot === 'panel') return bottom;
    if (slot === 'secondarySidebar') return right;
    return null;
  }
};

export { TabbedContainerManager, PanelTabsManager, panelContainers };
export default bottom;
